import { useCallback, useRef } from 'react';
import { isSortable } from '@dnd-kit/react/sortable';

/**
 * Factorise la logique DnD des cartes/listes (handleDragStart / handleDragEnd)
 * + le contournement removeChild (originalCardParentRef).
 * Réutilisé par le Kanban (Board) ET la vue hiérarchique (couloir feuilles).
 *
 * @param {object} params
 * @param {string} params.boardId
 * @param {(cardId, boardId, targetGroup, targetIndex) => void} params.onCardTransfer
 * @param {(cardId, group, index) => void} params.onCardMove
 * @param {(listId, index) => void} [params.onListMove] - optionnel (pas de tri listes dans les couloirs)
 */
export default function useCardDndHandlers({ boardId, onCardTransfer, onCardMove, onListMove }) {
  // track the card's original parent DOM node so we can revert the
  // OptimisticSortingPlugin's cross-container DOM move before React reconciles.
  // Otherwise it throws: "Failed to execute 'removeChild' on 'Node'".
  // See https://github.com/clauderic/dnd-kit/issues/1747
  const originalCardParentRef = useRef(null);

  const handleDragStart = useCallback((event) => {
    const { source } = event.operation;

    if (source && isSortable(source) && source.type === 'Card') {
      originalCardParentRef.current = source.sortable.element?.parentNode || null;
    }
  }, []);

  const handleDragEnd = useCallback(
    (event) => {
      const { source, target } = event.operation;

      if (event.canceled || !source || !isSortable(source)) {
        originalCardParentRef.current = null;
        return;
      }

      // list drag (only if onListMove provided)
      if (source.type === 'List') {
        if (onListMove && source.index !== source.initialIndex) {
          onListMove(source.id, source.index);
        }
        return;
      }

      // card drag
      if (source.type === 'Card') {
        // the OptimisticSortingPlugin physically moved the card's DOM to the
        // target list during dragover. Move it back before dispatching Redux
        // so React can reconcile without removeChild errors.
        const cardElement = source.sortable.element;
        const originalParent = originalCardParentRef.current;

        if (cardElement && originalParent && cardElement.parentNode !== originalParent) {
          cardElement.style.display = 'none';
          originalParent.appendChild(cardElement);
        }
        if (target && target.group !== source.initialGroup) {
          onCardTransfer(source.id, boardId, target.group, target.index);
        }
        // ─── Réordonnancement intra-liste désactivé (inutile : l'ordre découle
        // de la hiérarchie/position). Décommenter pour le réactiver. ───

        // else if (source.index !== source.initialIndex) {
        // onCardMove(source.id, source.group, source.index);
        // }

        originalCardParentRef.current = null;
      }
    },
    [boardId, onListMove, onCardTransfer],
  );

  return { handleDragStart, handleDragEnd };
}
